import { Component, Input, Output, EventEmitter, forwardRef, inject, signal, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { UtilService } from '@services/util.service';
import { MeterService } from '@services/meter.service';
import { LogService } from '@services/log.service';
import { MeterDetails } from '@models/meter.model';

export interface SmartMeterAgreementData {
  moveInDate: Date | null;
  termsAccepted: boolean;
}

@Component({
  selector: 'app-smart-meter-agreement',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SmartMeterAgreementComponent),
      multi: true
    }
  ],
  template: `
    <div class="smart-meter-agreement">
      <!-- Move-in Date - Only show if not provided as input -->
      @if (!moveInDate) {
        <div class="move-in-date-section">
          <h5>Move-in Date</h5>
          <p>When did you move in to this address?</p>
          <mat-form-field appearance="outline">
            <mat-label>Move-in date</mat-label>
            <input matInput [matDatepicker]="moveInPicker" [formControl]="moveInDateControl" [max]="maxDate">
            <mat-datepicker-toggle matIconSuffix [for]="moveInPicker"></mat-datepicker-toggle>
            <mat-datepicker #moveInPicker></mat-datepicker>
            @if (moveInDateControl.hasError('required') && moveInDateControl.touched) {
              <mat-error>Move-in date is required</mat-error>
            }
            @if (moveInDateControl.hasError('matDatepickerMax') && moveInDateControl.touched) {
              <mat-error>Move-in date cannot be in the future</mat-error>
            }
          </mat-form-field>
        </div>
      }

      <!-- Smart Meter Terms Acceptance -->
      <div class="smart-meter-terms-section">
        <h5>Smart Meter Terms</h5>
        @if (loadingTermsLink()) {
          <p>Loading terms and conditions...</p>
        } @else if (smartMeterTermsLink()) {
          <mat-checkbox [formControl]="smartMeterTermsControl">
            I agree to the 
            <a [href]="smartMeterTermsLink()" 
               target="_blank"
               rel="noopener noreferrer"
               class="terms-link">
              smart meter terms and conditions
            </a>
          </mat-checkbox>
        } @else {
          <mat-checkbox [formControl]="smartMeterTermsControl">
            I agree to the smart meter terms and conditions
          </mat-checkbox>
        }
      </div>
    </div>
  `,
  styleUrls: ['./smart-meter-agreement.component.scss']
})
export class SmartMeterAgreementComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() moveInDate: Date | null = null; // If provided, hide move-in date UI
  @Input() selectedMeters: MeterDetails[] = [];
  
  @Output() triggerAutoScroll = new EventEmitter<void>();
  
  private utilService = inject(UtilService);
  private meterService = inject(MeterService);
  private logService = inject(LogService);
  
  // Internal value
  private _value: SmartMeterAgreementData | null = null;
  
  // Control value accessor methods
  private _onChange = (value: SmartMeterAgreementData | null) => {};
  private _onTouched = () => {};
  
  // Smart meter terms link
  smartMeterTermsLink = signal<string | null>(null);
  loadingTermsLink = signal(false);
  
  // Maximum date for move-in date picker (today)
  maxDate = new Date();
  
  // Form controls
  moveInDateControl = new FormControl<Date | null>(null, [Validators.required]);
  smartMeterTermsControl = new FormControl(false, [Validators.requiredTrue]);
  
  ngOnInit(): void {
    // Load smart meter terms
    this.loadSmartMeterTerms();
    
    // Subscribe to form changes
    this.moveInDateControl.valueChanges.subscribe((selectedDate) => {
      // Log the date selection for debugging
      if (selectedDate) {
        this.logService.addConsentActionLog('MOVE_IN_DATE_SELECTED', {
          selectedDate: selectedDate.toString(),
          year: selectedDate.getFullYear(),
          month: selectedDate.getMonth() + 1,
          day: selectedDate.getDate(),
          isoString: selectedDate.toISOString(),
          isYearValid: selectedDate.getFullYear() >= 1900 && selectedDate.getFullYear() <= 2100
        });
      }
      this.updateValue();
      this.triggerAutoScrollEvent();
    });

    this.smartMeterTermsControl.valueChanges.subscribe(() => {
      this.updateValue();
      this.triggerAutoScrollEvent();
    });

    // If moveInDate is provided as input, set it and make the control not required
    if (this.moveInDate) {
      this.moveInDateControl.setValue(this.moveInDate);
      this.moveInDateControl.clearValidators();
      this.moveInDateControl.updateValueAndValidity();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to moveInDate input
    if (changes['moveInDate'] && this.moveInDateControl) {
      if (this.moveInDate) {
        this.moveInDateControl.setValue(this.moveInDate);
        this.moveInDateControl.clearValidators();
        this.moveInDateControl.updateValueAndValidity();
      } else {
        this.moveInDateControl.setValidators([Validators.required]);
        this.moveInDateControl.updateValueAndValidity();
      }
      this.updateValue();
    }

    // Handle changes to selectedMeters - only reload terms if meter numbers actually changed
    if (changes['selectedMeters'] && !changes['selectedMeters'].firstChange) {
      const oldMeters = changes['selectedMeters'].previousValue || [];
      const newMeters = changes['selectedMeters'].currentValue || [];
      
      // Only reload if the first meter number changed (we use first meter for terms)
      const oldFirstMeter = oldMeters.length > 0 ? oldMeters[0].meterNo : null;
      const newFirstMeter = newMeters.length > 0 ? newMeters[0].meterNo : null;
      
      if (oldFirstMeter !== newFirstMeter) {
        this.loadSmartMeterTerms();
      }
    }
  }

  private triggerAutoScrollEvent(): void {
    setTimeout(() => {
      this.triggerAutoScroll.emit();
    }, 100);
  }

  /**
   * Validate and correct a date if it has a 2-digit year (0-99)
   * JavaScript Date with years 0-99 creates dates in years 0-99 AD, not 1900s/2000s
   */
  private validateAndCorrectDate(date: Date | null): Date | null {
    if (!date) return null;
    
    const year = date.getFullYear();
    if (year >= 0 && year < 100) {
      // Correct 2-digit year: 00-30 -> 2000s, 31-99 -> 1900s
      const correctedYear = year <= 30 ? 2000 + year : 1900 + year;
      console.warn(`SmartMeterAgreement: Corrected 2-digit year ${year} to ${correctedYear}`);
      const correctedDate = new Date(date);
      correctedDate.setFullYear(correctedYear);
      return correctedDate;
    }
    
    return date;
  }

  private updateValue(): void {
    let effectiveMoveInDate = this.moveInDate || this.moveInDateControl.value;
    const termsAccepted = !!this.smartMeterTermsControl.value;
    
    // Validate and correct any 2-digit year dates
    effectiveMoveInDate = this.validateAndCorrectDate(effectiveMoveInDate);
    
    // Determine if all required fields are completed
    const isComplete = termsAccepted && (this.moveInDate || this.moveInDateControl.valid);
    
    if (isComplete && effectiveMoveInDate) {
      this._value = {
        moveInDate: effectiveMoveInDate,
        termsAccepted
      };
      this._onChange(this._value);
      this._onTouched();
    } else {
      this._value = null;
      this._onChange(null);
    }
  }

  // Load smart meter terms and conditions link
  private loadSmartMeterTerms(): void {
    if (this.selectedMeters.length === 0) {
      console.warn('No meters selected for terms loading');
      return;
    }

    // Use the first meter's number to get terms
    const meterNumber = this.selectedMeters[0].meterNo;
    
    this.loadingTermsLink.set(true);
    this.smartMeterTermsLink.set(null);

    this.meterService.getSmartMeterTerms(meterNumber).subscribe({
      next: (termsLink) => {
        this.smartMeterTermsLink.set(termsLink);
        this.loadingTermsLink.set(false);
      },
      error: (error) => {
        console.error('Error loading smart meter terms:', error);
        this.loadingTermsLink.set(false);
        this.utilService.openSnackBar('Failed to load terms and conditions', 'Error', 'error');
      }
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: SmartMeterAgreementData | null): void {
    this._value = value;
    
    if (value) {
      // Only set move-in date if not provided as input
      if (!this.moveInDate) {
        this.moveInDateControl.setValue(value.moveInDate, { emitEvent: false });
      }
      this.smartMeterTermsControl.setValue(value.termsAccepted, { emitEvent: false });
    } else {
      if (!this.moveInDate) {
        this.moveInDateControl.reset(null, { emitEvent: false });
      }
      this.smartMeterTermsControl.reset(false, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: SmartMeterAgreementData | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.moveInDateControl.disable();
      this.smartMeterTermsControl.disable();
    } else {
      this.moveInDateControl.enable();
      this.smartMeterTermsControl.enable();
    }
  }
}
